import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';

// Глобальное расширение для сохранения классов и inline-стилей во время редактирования
const PreserveAttributesExtension = Extension.create({
  name: 'preserveAttributes',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'listItem', 'blockquote'],
        attributes: {
          class: {
            default: null,
            parseHTML: element => element.getAttribute('class'),
            renderHTML: attributes => {
              if (!attributes.class) return {};
              return { class: attributes.class };
            },
          },
          style: {
            default: null,
            parseHTML: element => element.getAttribute('style'),
            renderHTML: attributes => {
              if (!attributes.style) return {};
              return { style: attributes.style };
            },
          },
        },
      },
    ];
  },
});

function trimEmptyEndingParagraph(html) {
  if (!html) return '';
  const cleared = html.trim().replace(/(<p><\/p>|<p><br\s*\/?><\/p>|<p>&nbsp;<\/p>)+$/, '');
  // Если после очистки пусто, возвращаем исходный пустой параграф
  return cleared === '' ? '<p></p>' : cleared;
}

/**
 * Mount a single Tiptap instance on the given element.
 * @param {HTMLElement} el  - the [data-tiptap] div
 */
function mountTiptap(el) {
  if (el.dataset.tiptapMounted) return;
  el.dataset.tiptapMounted = 'true';

  const toolbarId = el.dataset.toolbar;
  const inputId   = el.dataset.input;
  const placeholder = el.dataset.placeholder || 'Введите текст…';

  const toolbar   = toolbarId ? document.getElementById(toolbarId) : null;
  const hiddenInput = inputId ? document.getElementById(inputId) : null;

  // Initial content from hidden input (existing value) or empty
  const initialContent = hiddenInput && hiddenInput.value
    ? hiddenInput.value
    : '';

  const editor = new Editor({
    element: el,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
      PreserveAttributesExtension,
    ],
    content: initialContent,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    onUpdate({ editor }) {
      if (hiddenInput) {
        hiddenInput.value = trimEmptyEndingParagraph(editor.getHTML());
      }
      syncToolbar(editor, toolbar);
    },
    onSelectionUpdate({ editor }) {
      syncToolbar(editor, toolbar);
    },
  });

  // Expose for debug: window.tiptapEditors is an array
  window.tiptapEditors = window.tiptapEditors || [];
  window.tiptapEditors.push(editor);

  // HTML mode state tracker
  let isHtmlMode = false;
  const parent = el.parentNode;
  let textarea = parent.querySelector('.tiptap-html-textarea');
  if (!textarea) {
    textarea = document.createElement('textarea');
    textarea.className = 'form-control d-none tiptap-html-textarea';
    textarea.style.minHeight = '150px';
    textarea.style.fontFamily = 'monospace';
    parent.insertBefore(textarea, el.nextSibling);
  }

  const handleHtmlModeToggle = () => {
    isHtmlMode = !isHtmlMode;
    if (isHtmlMode) {
      const rawHtml = trimEmptyEndingParagraph(editor.getHTML());
      textarea.value = rawHtml;
      
      el.classList.add('d-none');
      textarea.classList.remove('d-none');
      
      textarea.addEventListener('input', updateFromTextarea);
      
      if (toolbar) {
        const htmlBtn = toolbar.querySelector('[data-command="html"]');
        if (htmlBtn) htmlBtn.classList.add('active');
        toolbar.querySelectorAll('[data-command]:not([data-command="html"])').forEach(btn => {
          btn.disabled = true;
        });
      }
    } else {
      const rawHtml = textarea.value;
      editor.commands.setContent(rawHtml, { parseOptions: { preserveWhitespace: 'full' } });
      if (hiddenInput) {
        hiddenInput.value = trimEmptyEndingParagraph(rawHtml);
      }
      
      textarea.classList.add('d-none');
      el.classList.remove('d-none');
      
      textarea.removeEventListener('input', updateFromTextarea);
      
      if (toolbar) {
        const htmlBtn = toolbar.querySelector('[data-command="html"]');
        if (htmlBtn) htmlBtn.classList.remove('active');
        toolbar.querySelectorAll('[data-command]').forEach(btn => {
          btn.disabled = false;
        });
      }
      syncToolbar(editor, toolbar);
    }
  };

  function updateFromTextarea() {
    if (hiddenInput) {
      hiddenInput.value = textarea.value;
    }
  }

  if (toolbar) {
    // Prevent focus loss on mousedown
    toolbar.addEventListener('mousedown', (event) => {
      if (event.target.closest('[data-command]')) {
        event.preventDefault();
      }
    });

    toolbar.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-command]');
      if (!btn) return;
      
      const cmd = btn.dataset.command;
      if (cmd === 'html') {
        handleHtmlModeToggle();
      } else {
        if (!isHtmlMode) {
          runCommand(editor, btn.dataset.command);
          syncToolbar(editor, toolbar);
        }
      }
    });
  }

  syncToolbar(editor, toolbar);
}

function syncToolbar(editor, toolbar) {
  if (!toolbar) return;
  toolbar.querySelectorAll('[data-command]').forEach((btn) => {
    const cmd = btn.dataset.command;
    const active = ({
      bold:        () => editor.isActive('bold'),
      italic:      () => editor.isActive('italic'),
      underline:   () => editor.isActive('underline'),
      strike:      () => editor.isActive('strike'),
      code:        () => editor.isActive('code'),
      heading1:    () => editor.isActive('heading', { level: 1 }),
      heading2:    () => editor.isActive('heading', { level: 2 }),
      bulletList:  () => editor.isActive('bulletList'),
      orderedList: () => editor.isActive('orderedList'),
      blockquote:  () => editor.isActive('blockquote'),
    })[cmd];
    btn.classList.toggle('active', active ? active() : false);
  });
}

function runCommand(editor, cmd) {
  switch (cmd) {
    case 'bold':        editor.chain().focus().toggleBold().run(); break;
    case 'italic':      editor.chain().focus().toggleItalic().run(); break;
    case 'underline':   editor.chain().focus().toggleUnderline().run(); break;
    case 'strike':      editor.chain().focus().toggleStrike().run(); break;
    case 'code':        editor.chain().focus().toggleCode().run(); break;
    case 'heading1':    editor.chain().focus().toggleHeading({ level: 1 }).run(); break;
    case 'heading2':    editor.chain().focus().toggleHeading({ level: 2 }).run(); break;
    case 'bulletList':  editor.chain().focus().toggleBulletList().run(); break;
    case 'orderedList': editor.chain().focus().toggleOrderedList().run(); break;
    case 'blockquote':  editor.chain().focus().toggleBlockquote().run(); break;
    case 'undo':        editor.chain().focus().undo().run(); break;
    case 'redo':        editor.chain().focus().redo().run(); break;
    case 'clear':       editor.chain().focus().clearNodes().unsetAllMarks().run(); break;
    case 'createLink': {
      let url = prompt('Введите ссылку (URL)', 'https://');
      if (url) {
        if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
      break;
    }
    case 'unsetLink': editor.chain().focus().unsetLink().run(); break;
  }
}

// ── Auto-init with MutationObserver ──────────────────────────────────────────
// Initializes all [data-tiptap] elements (form fields + standalone editors)
// including dynamically added ones via AJAX/MutationObserver.
function initTiptapOnElements(root) {
  root.querySelectorAll('[data-tiptap]').forEach(mountTiptap);

  // Legacy standalone test editor (#tiptap-editor without data-tiptap)
  const legacyEl = root.querySelector('#tiptap-editor:not([data-tiptap])');
  if (legacyEl) {
    const legacyToolbar = root.querySelector('#tiptap-toolbar');
    legacyEl.dataset.tiptap = '';
    if (legacyToolbar) legacyEl.dataset.toolbar = legacyToolbar.id;
    mountTiptap(legacyEl);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initTiptapOnElements(document.body);

  // Наблюдатель за изменениями DOM для поддержки динамически подгружаемых элементов
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Если сам добавленный узел является редактором
            if (node.hasAttribute('data-tiptap') || (node.id === 'tiptap-editor' && !node.hasAttribute('data-tiptap'))) {
              mountTiptap(node);
            }
            // Также ищем внутри добавленного узла
            node.querySelectorAll('[data-tiptap]').forEach(mountTiptap);
            const legacyEl = node.querySelector('#tiptap-editor:not([data-tiptap])');
            if (legacyEl) {
              const legacyToolbar = node.querySelector('#tiptap-toolbar');
              legacyEl.dataset.tiptap = '';
              if (legacyToolbar) legacyEl.dataset.toolbar = legacyToolbar.id;
              mountTiptap(legacyEl);
            }
          }
        });
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
